import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import Modal from '@minecraft.modal';
import { ClickableIcon } from '@minecraft.atoms';

const StyledGridRow = styled.div<{
  rowNumber: number;
  rowSpan: number;
}>`
  grid-row: ${(props) => props.rowNumber} / span ${(props) => props.rowSpan};
`;

const StyledGridColumn = styled.div<{
  colNumber: number;
  colSpan: number;
}>`
  grid-column: ${(props) => props.colNumber} / span ${(props) => props.colSpan};
`;

const StyledTalentScoutHeader = styled(StyledGridColumn)`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-end;
`;

const StyledTalentScoutLogo = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
`;

const StyledBanner = styled.div`
  background-color: ${({ theme }) => theme.colors.backgrounds.lightBlue};
  display: grid;
  grid-template-columns: min-content auto;
  grid-template-rows: auto auto;
  width: 100%;
`;

export const Banner: FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const toggle = () => setModalOpen(!modalOpen);

  return (
    <StyledBanner data-testid="talent-scout-banner-testId">
      <StyledGridRow rowNumber={1} rowSpan={2}>
        <StyledTalentScoutLogo
          aria-label="logo"
          className="cn_atom_pl-4 cn_atom_py-2"
          data-testid="talent-scout-logo-testId"
        >
          <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M33.2196 4.36011L4.65283 32.9268L33.0732 61.3472L61.5424 32.9756L33.2196 4.36011Z" fill="white" />
            <path
              d="M7.01917 34.9272C5.99458 33.9026 5.99458 32.073 7.01917 31.0484L31.0728 6.99478C32.1462 5.92139 33.8782 5.92139 34.9516 6.99478L55.9071 27.9502L58.3222 25.5351L37.3668 4.57966C36.1958 3.40869 34.6589 2.77441 33 2.77441C31.3411 2.77441 29.8043 3.40869 28.6333 4.57966L4.57966 28.6333C3.40869 29.8043 2.77441 31.3411 2.77441 33C2.77441 34.6589 3.40869 36.1958 4.57966 37.3668L18.3629 51.15C18.4361 49.6619 18.6313 48.2226 18.924 46.8809L7.01917 34.9272Z"
              fill="url(#paint0_linear_1548_74906)"
            />
            <path
              d="M63.2012 33C63.2012 31.3411 62.567 29.8042 61.396 28.6332L58.2978 25.535L55.8827 27.9502L58.9809 31.0483C59.4932 31.5606 59.7859 32.2437 59.7859 32.9756C59.7859 33.7074 59.4932 34.3905 58.9809 34.9028L47.0516 46.832C47.3444 48.1981 47.5395 49.6131 47.6127 51.1012L61.396 37.3179C62.567 36.1957 63.2012 34.6344 63.2012 33Z"
              fill="url(#paint1_linear_1548_74906)"
            />
            <path
              d="M44.4657 49.418L34.9272 58.9565C33.8538 60.0299 32.1218 60.0299 31.0484 58.9565L21.5099 49.418C21.3635 50.345 21.2659 51.3208 21.2659 52.321V54.0287L27.9258 60.6885L28.6333 61.396C29.8042 62.567 31.3411 63.2012 33 63.2012C34.6589 63.2012 36.1958 62.567 37.3667 61.396L38.0742 60.6885L44.7341 54.0287V52.321C44.7097 51.3208 44.6121 50.3694 44.4657 49.418Z"
              fill="url(#paint2_linear_1548_74906)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M38.0986 35.7322C42.2702 36.4397 45.7099 40.8796 47.0516 46.8564C47.3444 48.2225 47.5395 49.6374 47.6371 51.1255L44.7341 54.0286V52.3209C44.7341 51.3207 44.6365 50.3449 44.4902 49.4179C43.6363 44.1241 40.5625 40.1721 36.9033 40.1721H29.0968C25.4375 40.1721 22.3637 44.1241 21.5343 49.4179C21.3879 50.3449 21.2904 51.3207 21.2904 52.3209V54.0286L18.3873 51.1255C18.4605 49.6374 18.6557 48.2225 18.9484 46.8564C20.2902 40.8796 23.7299 36.4397 27.9014 35.7322C24.8277 33.927 22.7541 30.5116 22.7541 26.6084C22.7541 20.778 27.3404 16.0697 33 16.0697C38.6597 16.0697 43.246 20.8024 43.246 26.6084C43.246 30.5116 41.1724 33.927 38.0986 35.7322ZM25.6815 26.6084C25.6815 30.7556 28.9504 34.1221 33 34.1221C37.0496 34.1221 40.3186 30.7556 40.3186 26.6084C40.3186 22.4612 37.0496 19.0947 33 19.0947C28.9504 19.0947 25.6815 22.4612 25.6815 26.6084Z"
              fill="#010101"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1548_74906"
                x1="0.652117"
                y1="-0.7209"
                x2="0.652117"
                y2="51.3473"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0090D6" />
                <stop offset="1" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_1548_74906"
                x1="38.6732"
                y1="-0.720966"
                x2="38.6732"
                y2="51.3472"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0090D6" />
                <stop offset="1" />
              </linearGradient>
              <linearGradient
                id="paint2_linear_1548_74906"
                x1="-552.618"
                y1="199.214"
                x2="-65.9865"
                y2="199.214"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0090D6" />
                <stop offset="1" />
              </linearGradient>
            </defs>
          </svg>
        </StyledTalentScoutLogo>
      </StyledGridRow>
      <StyledTalentScoutHeader colNumber={2} colSpan={1}>
        <h2 className="cn_atom_p-0 cn_m-0 cn_atom_pl-1 cn_mb-1">{t('reps:talentScout.title')}</h2>
        <ClickableIcon
          isSmall
          className="cn_ml-2"
          onClick={toggle}
          aria-label={t('reps:talentScout.information')}
          name="info-filled"
        />

        {modalOpen && (
          <Modal
            isOpen={modalOpen}
            modalSize="large"
            title={t('reps:talentScout.title')}
            isFooterHidden
            handleToggleClick={toggle}
            data-testid="more-info-modal-testId"
          >
            {t('reps:talentScout.description')}
          </Modal>
        )}
      </StyledTalentScoutHeader>
      <StyledGridColumn colNumber={2} colSpan={1}>
        <p className="cn_atom_p-0 cn_m-0 cn_atom_pl-1">{t('reps:talentScout.subTitle')}</p>
      </StyledGridColumn>
    </StyledBanner>
  );
};
